import { useState, useEffect } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import { EventTypes, logEvent } from '@unified-lk/event-log'
import type { ConstructorPlan } from '../constructorTypes'
import { PublicShareForm } from '../shared/PublicShareForm'
import type { PublicShareFormValue } from '../shared/PublicShareForm'

export function SharePanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const share = plan.share ?? {}
  const shareInPatientLk = plan.share_in_patient_lk ?? (share.is_active ?? false)

  const [formValue, setFormValue] = useState<PublicShareFormValue>({
    is_active: share.is_active ?? false,
    expires_at: share.expires_at ?? null,
    hide_prices: share.hide_prices ?? false,
    access_password: null,
    payment: (share.payment as PublicShareFormValue['payment']) ?? null,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setFormValue((prev) => ({
      ...prev,
      is_active: share.is_active ?? false,
      expires_at: share.expires_at ?? null,
      hide_prices: share.hide_prices ?? false,
      payment: (share.payment as PublicShareFormValue['payment']) ?? null,
    }))
    setError('')
  }, [share.expires_at, share.hide_prices, share.payment, share.is_active])

  async function handleActivate() {
    if (!formValue.expires_at?.trim()) {
      setError('Укажите срок действия ссылки.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await staffApi.activateConstructorShare(bootstrap, session, plan.id, {
        expiresAt: formValue.expires_at || undefined,
        hidePrices: formValue.hide_prices,
        payment: formValue.payment ?? undefined,
        accessPassword: formValue.access_password ?? undefined,
      })
      logEvent(bootstrap.apiBase, session.accessToken, EventTypes.CONSTRUCTOR_SHARE_ACTIVATED, { plan_id: plan.id })
      onReload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить настройки шеринга')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeactivate() {
    setSaving(true)
    setError('')
    try {
      await staffApi.deactivateConstructorShare(bootstrap, session, plan.id)
      onReload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось деактивировать ссылку')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-1 text-sm font-semibold text-slate-800">Шеринг плана</h3>
      <p className="mb-3 text-[11px] text-slate-500">
        Ссылка для пациента: <code>/?secret=&lt;код&gt;</code> · код 7-8 символов.
      </p>

      <div className={`mb-3 rounded-lg px-3 py-2 text-xs flex items-center gap-2 ${shareInPatientLk ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-500'}`}>
        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {shareInPatientLk ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          )}
        </svg>
        {shareInPatientLk ? 'План доступен пациенту в ЛК' : 'План скрыт от пациента в ЛК'}
      </div>

      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-2.5 w-2.5 rounded-full ${share.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
        <span className="text-xs text-slate-600">
          {share.is_active ? 'Публичная ссылка активна' : 'Публичная ссылка не активна'}
        </span>
      </div>

      <PublicShareForm
        value={formValue}
        onChange={(patch) => setFormValue((prev) => ({ ...prev, ...patch }))}
        publicUrl={share.url ?? null}
        hasPassword={(share as Record<string, unknown>).has_password as boolean | undefined}
        onActivate={() => void handleActivate()}
        onDeactivate={() => void handleDeactivate()}
        saving={saving}
        error={error || undefined}
        bootstrap={bootstrap}
        session={session}
      />
    </div>
  )
}
